import { Component, OnInit } from '@angular/core';

import { AdministrationService } from './administration.service';
import {
  AdminUserData,
  AdminUserResponse,
  CreateAdminRequestData,
  CreateAdminResponse,
  DeleteAdminResponse,
  RolePermissionGroup,
} from './administration.domain';
import { ErrorService } from '../services/error/error.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LookupOptionsData } from '../jurisdiction/jurisdiction.domain';
import { AllDropdownKeys, TableConfig } from '../constants/app.constants';
import { CommonService } from '../services/common.service';
import { LocalStorageService } from '../services/local-storage.service';
import { TableConfiguration } from '../domains/common.domain';
import { ConfirmationService } from 'primeng/api';
import { ValidationsService } from '../services/validations.service';
import { ZerezConfigData, ZerezService } from '../services/zerez.service';

@Component({
  providers: [AdministrationService, ConfirmationService, ZerezService],
  selector: 'app-administration',
  styleUrls: ['./administration.component.scss'],
  templateUrl: './administration.component.html',
})
export class AdministrationComponent implements OnInit {
  adminData: AdminUserData[] = [];
  adminFG: FormGroup;
  canChangePermissions: boolean = false;
  currentTechnicianId: string;
  filterFields: string[] = ['name', 'email', 'role'];
  globalFilterValue: string;
  isLoading: boolean;
  permissionsData: RolePermissionGroup[];
  roleTypes: LookupOptionsData[];
  roleTypesKey: string = AllDropdownKeys.AdminRole;
  showPassword: boolean = false;
  tableConfig: TableConfiguration = TableConfig;
  zerezData: ZerezConfigData = { client_id: '', client_secret: '', last_changed: '' };
  zerezFG: FormGroup;

  constructor(
    private administrationService: AdministrationService,
    private zerezService: ZerezService,
    private commonService: CommonService,
    private confirmationService: ConfirmationService,
    private errorService: ErrorService,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.roleTypes = this.commonService.getDropdownData(this.roleTypesKey);
    this.currentTechnicianId = this.localStorageService.getItem('technician_id');

    this.prepareForm();

    this.getAdminUserData();
    this.getPermissionGroupData();
    this.getZerezData();
  }

  addAdminUser(): void {
    if (this.adminFG.valid) {
      const requestData: CreateAdminRequestData = {
        email: this.adminFG.value.email,
        password: this.adminFG.value.password,
        role: this.adminFG.value.role,
        first_name: this.adminFG.value.firstName,
        last_name: this.adminFG.value.lastName,
      };
      this.administrationService.createNewAdmin(requestData).subscribe(
        (response: CreateAdminResponse) => {
          this.errorService.throwErrorIfFailure(response, true);
          this.adminFG.reset();
          this.getAdminUserData();
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    }
  }

  deleteAdminUser(technicianId: string): void {
    this.confirmationService.confirm({
      accept: () => {
        this.confirmationService.close();
        this.administrationService.deleteAdminUser(technicianId).subscribe(
          (response: DeleteAdminResponse) => {
            this.errorService.throwErrorIfFailure(response, true);
            this.getAdminUserData();
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      },
      reject: () => {
        this.confirmationService.close();
      },
    });
  }

  showDeleteIcon(technicianData: AdminUserData): boolean {
    let isRoleMatch = false;

    ['employee', 'observer', 'administrator'].forEach(role => {
      isRoleMatch = isRoleMatch || technicianData.role === role;
    });

    return technicianData.technician_id !== this.currentTechnicianId && isRoleMatch;
  }

  updateZerezConfig(): void {
    this.isLoading = true;

    var payload = <ZerezConfigData>{
      client_id: this.zerezFG.get('clientId').value,
      client_secret: this.zerezFG.get('clientSecret').value,
    };
    this.zerezService.updateZerezData(payload).subscribe(
      response => {
        this.errorService.throwErrorIfFailure(response, true);
        this.zerezData = response;
        this.zerezFG.reset();
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
        this.isLoading = false;
      },
      () => (this.isLoading = false)
    );
  }

  private getAdminUserData(): void {
    this.isLoading = true;
    this.administrationService.getAllAdminUsers().subscribe(
      (response: AdminUserResponse) => {
        this.errorService.throwErrorIfFailure(response, true);
        this.adminData = response.adminuser.map(item => {
          item.name = `${item.first_name} ${item.last_name}`;
          return item;
        });
        this.isLoading = false;
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private prepareForm(): void {
    this.adminFG = new FormGroup({
      email: new FormControl('', [Validators.required, ValidationsService.emailValidator()]),
      password: new FormControl('', [Validators.required]),
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      role: new FormControl('', [Validators.required]),
    });
    this.zerezFG = new FormGroup({
      clientId: new FormControl('', [Validators.required]),
      clientSecret: new FormControl('', [Validators.required]),
      lastChanged: new FormControl(''),
    });
  }

  private getPermissionGroupData(): void {
    this.isLoading = true;

    this.administrationService.getPermissionGroupData().subscribe(
      response => {
        this.errorService.throwErrorIfFailure(response, true);
        this.permissionsData = response;
      },
      error => this.errorService.throwErrorIfFailure(error, true),
      () => (this.isLoading = false)
    );
  }

  private getZerezData(): void {
    this.isLoading = true;

    this.zerezService.getZerezData().subscribe(
      response => {
        this.errorService.throwErrorIfFailure(response, true);
        this.zerezData = response;
      },
      error => this.errorService.throwErrorIfFailure(error, true),
      () => (this.isLoading = false)
    );
  }
}
